import React, { useMemo } from 'react'
import _ from 'lodash'
import { Row } from 'react-bootstrap'
import CustomHighcharts from 'sharedComponents/CustomHighcharts'
import ListTable from 'sharedComponents/ListTable'
import EmptyMessage from 'sharedComponents/EmptyMessage'
import { convertToOverviewMetrics, convertFacetToListTableConfig } from 'modules/formatting'

interface Props {
  reportData: any
}

const noop = () => undefined

/**
 * Breakdown ("charts") report body for the chat preview panel. Breakdown results are raw Solr facets, so they
 * go through the same converters the Reports page uses (convertToOverviewMetrics / convertFacetToListTableConfig)
 * and the same chart + list table components, one block per configured breakdown, in a single column.
 */
const BreakdownReportView: React.FC<Props> = ({ reportData }) => {
  const { stats, facets, trends, trendsHour } = _.get(reportData, 'data') || {}
  const chartConfigs = _.get(reportData, ['config', 'config', 'charts'], {})
  const trendKeys = _.get(reportData, ['config', 'params', 'trend_keys'])

  const summary: any = useMemo(
    () => convertToOverviewMetrics(stats, facets, trends, trendsHour, ['USD'], null, chartConfigs, trendKeys),
    [stats, facets, trends, trendsHour, chartConfigs, trendKeys],
  )

  const blocks = Object.keys(chartConfigs)
    .filter((facetKey) => summary?.facets?.[facetKey])
    .map((facetKey) => {
      const facet = summary.facets[facetKey]
      const metricKeys = [
        ...(_.get(facet, 'chart_type', '') !== 'none' ? [_.get(facet, 'metric', '')] : []),
        ...Object.keys(_.get(facet, 'extraMetricConfigs', {})).filter(
          (key) => _.get(facet, ['extraMetricConfigs', key, 'chart_type'], '') !== 'none',
        ),
      ].filter(Boolean)
      const table = convertFacetToListTableConfig(facet)
      return { facetKey, facet, metricKeys, table }
    })
    .filter((b) => b.metricKeys.length || b.table?.data?.length)

  if (!blocks.length) return <EmptyMessage msg="No data to display for this date range" />

  return (
    <div className="tw-flex tw-flex-col tw-gap-6">
      {blocks.map(({ facetKey, facet, metricKeys, table }) => (
        <div key={facetKey}>
          {metricKeys.length > 0 && (
            <Row className="chart-grid" style={{ margin: '0 -6px' }}>
              {metricKeys.map((mk) => (
                <CustomHighcharts
                  key={`${facetKey}-${mk}`}
                  facet={{ ...facet, ...(facet.extraMetricConfigs || {})[mk], title: '' }}
                  facetName={mk}
                  legend={false}
                  height={260}
                  cols={{ xs: 12, sm: 12, md: 12, lg: 12 }}
                  currentFilter={[]}
                  setChartTypeCallback={noop}
                  style={{ marginLeft: 6, marginRight: 6 }}
                  chartTypes={[]}
                  limitedAccess={false}
                  checkAccess={noop}
                />
              ))}
            </Row>
          )}
          {table?.data?.length > 0 && (
            <div className="scrollable-table-container tw-mt-3">
              <ListTable {...table} className="list-table" />
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

export default BreakdownReportView
